import { POSITIONS, positionLabelKey, isAdminPosition, isManagerPlus, normalizePosition, outranks } from "../access/positions";
import { X, KeyRound, UserX, Trash2, LockKeyhole, Check, Loader2, ShieldCheck, ShieldOff } from "lucide-react";
import { useState } from "react";
import "../styles/add-user.css";
import { displayUserType } from "../types/User";
import { useTranslation } from "../i18n";
import { useReadOnly } from "../access/ReadOnly";
import PasswordFields from "./PasswordFields";
import { useUsernameAvailability } from "../hooks/useUsernameAvailability";
import { isPolicyCompliant, passwordPolicyFor } from "./helpers/passwordPolicy";
import { normaliseUsername, usernameErrorKey, USERNAME_MAX } from "./helpers/usernameRules";
import { isValidEmail } from "./helpers/emailFormat";
import TwoFactorResetDialog from "./users/TwoFactorResetDialog";
import type { TwoFactorResetOutcome } from "./users/TwoFactorResetDialog";

// Edit User modal. Wider and two-column since 2026-10-20 so it fits one screen: the quick
// actions and the two-factor line sit above the form, the fields pair up (name / username,
// warehouse / position, email / RFID), the multi-device toggle and Save span the width.
//
// Who may do what follows the downstream rule (access/positions outranks, mirrored on the BE):
// an Admin manages every account below Admin, a Manager every account below Manager, and
// nobody touches a peer. Your own record opens for name / email / username / warehouse only.
// The list already hides the pencil for rows the viewer may not touch; the checks here are
// the second line, so a stale list cannot offer an action the server will refuse.
//
// Username rollout (2026-10-19): the modal also edits the LOGIN HANDLE (saved through its own
// endpoint by the page — see users.tsx editUser) and offers "Reset password" for every account
// the viewer outranks (an Admin resets a Manager; Admin passwords stay on the CLI script),
// which swaps the form for a one-field panel so the card keeps a single primary action.
const readSelfUid = (): number | null => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return null;
        const u = JSON.parse(raw);
        return typeof u?.uid === "number" ? u.uid : null;
    } catch { return null; }
};

const readSelfPermissions = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        if (!raw) return "";
        const u = JSON.parse(raw);
        return (typeof u?.position === "string" && u.position) || u?.permissions || "";
    } catch { return ""; }
};

export interface ResetPasswordPayload {
    newPassword: string;
    mustChangePassword: boolean;
}

interface EditUserProps {
    user: displayUserType;
    editUser: (user: displayUserType) => Promise<void>;
    toggleEdit: ({ user, changesMade }: { user: displayUserType, changesMade: boolean }) => void;
    /** Page-level handlers — these surface the same confirmation modal
     *  that the list rows use. Defined on the page so the confirm can
     *  pop above this modal cleanly. */
    requestForce: (user: displayUserType) => void;
    requestSuspend: (user: displayUserType) => void;
    requestUnsuspend: (user: displayUserType) => void;
    /** Delete uses its own dedicated confirm modal (`<DeleteUser>`),
     *  so it just needs the existing toggle. */
    toggleDelete: (user: displayUserType | null) => void;
    /** Sets a (temporary by default) password for an account below the viewer.
     *  Resolves true on success so the panel can close. */
    resetPassword: (user: displayUserType, payload: ResetPasswordPayload) => Promise<boolean>;
    /** Admin-only (2026-10-20): clears the user's authenticator and backup
     *  codes so they set two-factor up again at their next sign-in. May
     *  come back asking for the acting Admin's own code (step-up). Optional
     *  so the modal renders without it. */
    resetTwoFactor?: (user: displayUserType, adminOtp?: string) => Promise<TwoFactorResetOutcome>;
}

const EditUser = ({ user, editUser, toggleEdit, requestForce, requestSuspend, requestUnsuspend, toggleDelete, resetPassword, resetTwoFactor }: EditUserProps) => {
    const { t } = useTranslation();
    // Access-control read-only handheld mode (ACCESS_CONTROL_PLAN.md §8.4).
    const readOnly = useReadOnly();

    const [userName, set_userName] = useState<string>(user.name);
    // Login handle (NOT the display name). Empty = leave for the user to
    // choose at their next login.
    const [username, set_username] = useState<string>(user.username ?? "");
    const [userWarehouse, set_userWarehouse] = useState<string>(user.warehouse);
    // Normalised so a legacy rank on the document selects its position in the picker.
    const [userPermissions, set_userPermissions] = useState<string>(normalizePosition(user.permissions) ?? user.permissions);
    // The RFID tag is optional on the document (null / absent for anyone who never had a tag
    // assigned); the form state must be a string or Save crashes on `.trim()`.
    const [userRFID, setUserRFID] = useState<string>(user.userRFID ?? "");
    const [userEmail, set_userEmail] = useState<string>(user.email ?? "");
    const [allowConcurrent, set_allowConcurrent] = useState<boolean>(user.allowConcurrentSessions === true);
    const [loading, set_Loading] = useState(false);

    // Reset-password panel state.
    const [showReset, set_showReset] = useState(false);
    const [resetValue, set_resetValue] = useState("");
    const [requireChange, set_requireChange] = useState(true);
    const [resetting, set_resetting] = useState(false);
    // Two-factor reset dialog (confirm → step-up code).
    const [tfaDialog, set_tfaDialog] = useState(false);

    const selfUid = readSelfUid();
    const selfPermissions = readSelfPermissions();
    const selfIsAdmin = isAdminPosition(selfPermissions);
    const isSelf = selfUid != null && user.UID === selfUid;
    const targetIsAdmin = isAdminPosition(user.permissions);
    const isSuspended = user.status === "suspended";
    // Downstream rule: everything below is gated on it, self excepted where noted.
    const manages = outranks(selfPermissions, user.permissions);
    const showForceChange = manages && !isSelf && user.mustChangePassword !== true;
    const showSuspendToggle = manages && !isSelf;
    const showDelete = manages && !isSelf;
    const showResetAction = manages && !isSelf;
    // Nobody changes their own position; an Admin's is pinned (the BE pins it too).
    const positionLocked = isSelf || targetIsAdmin;
    // Only positions the viewer outranks can be assigned; an Admin may also make a peer Admin.
    const assignable = POSITIONS.filter((p) => outranks(selfPermissions, p) || (selfIsAdmin && p === "Admin"));
    const positionOptions = assignable.includes(userPermissions as typeof POSITIONS[number]) ? assignable : [userPermissions as typeof POSITIONS[number], ...assignable];
    // Waiving the one-session-per-user rule weakens the policy for that
    // account, so it's Admin-only — matching the BE, which drops the field
    // from any other actor's payload.
    const showConcurrentToggle = selfIsAdmin;
    // Two-factor (2026-10-20): the status line shows for anyone the rule
    // applies to (or who enrolled anyway); the reset is Admin-only, never on
    // yourself, and only when there is an authenticator to clear. Admin → Admin
    // is allowed here on purpose: it is the recovery path for a lost phone.
    const twoFactorApplies = user.twoFactorRequired === true || Boolean(user.twoFactorEnrolledAt);
    const showTwoFactorReset = selfIsAdmin && !isSelf && Boolean(user.twoFactorEnrolledAt) && typeof resetTwoFactor === "function";
    const enrolledOn = user.twoFactorEnrolledAt
        ? new Date(user.twoFactorEnrolledAt).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
        : "";

    const availability = useUsernameAvailability(username, { enabled: true, ignore: user.username ?? "", excludeUID: user.UID });
    const usernameBlocksSave = availability.status === "checking" || availability.status === "invalid" || availability.status === "unavailable";
    const editedManagerPlus = isManagerPlus(userPermissions);
    const emailTrimmed = userEmail.trim();
    const resetPolicy = passwordPolicyFor(user.permissions);
    const resetContext = { username: user.username ?? null, name: user.name };

    if (user.name === "") {
        return <></>;
    }

    const handleClose = () => {
        toggleEdit({ user: { _id: "", UID: 0, name: "", permissions: "", warehouse: "", userRFID: "" }, changesMade: false });
    };

    const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (usernameBlocksSave) return;

        // Set loading state
        set_Loading(true);

        //get form data
        const data: displayUserType = {
            _id: user._id,
            UID: user.UID,
            name: userName.trim(),
            username: username === "" ? null : username,
            // A locked position is sent back unchanged; pinned here too so a
            // stale state value can't slip through.
            permissions: positionLocked ? (normalizePosition(user.permissions) ?? user.permissions) : userPermissions,
            warehouse: userWarehouse,
            userRFID: userRFID.trim(),
            email: emailTrimmed === "" ? null : emailTrimmed,
            // Left undefined when the control wasn't offered, so the
            // page omits the field and the BE leaves it untouched.
            allowConcurrentSessions: showConcurrentToggle ? allowConcurrent : undefined,
        };

        // Edit User
        editUser(data);

        // Reset Loading State
        set_Loading(false);

    };

    const handleResetSubmit = async (event: React.SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!isPolicyCompliant(resetValue, resetPolicy, resetContext) || resetting) return;
        set_resetting(true);
        try {
            const ok = await resetPassword(user, { newPassword: resetValue, mustChangePassword: requireChange });
            if (ok) {
                set_resetValue("");
                set_requireChange(true);
                set_showReset(false);
            }
        } finally {
            set_resetting(false);
        }
    };

    const usernameHint = () => {
        const code = availability.status === "invalid" || availability.status === "unavailable" ? availability.code : null;
        if (code) return <span className="user-edit-field-hint user-edit-field-hint--error">{t(usernameErrorKey(code))}</span>;
        if (availability.status === "checking") return <span className="user-edit-field-hint"><Loader2 size={12} aria-hidden="true" /> {t("username_checking")}</span>;
        if (availability.status === "available") return <span className="user-edit-field-hint user-edit-field-hint--ok"><Check size={12} aria-hidden="true" /> {t("username_available")}</span>;
        return <span className="user-edit-field-hint">{t("users_username_hint")}</span>;
    };

    let submitButton = loading
        ? <button type="submit" className="btn btn-primary btn-full modal-form-span" disabled>{t("saving")}</button>
        : (
            <button
                type="submit"
                className="btn btn-primary btn-full modal-form-span"
                disabled={readOnly || usernameBlocksSave}
                aria-disabled={readOnly || usernameBlocksSave}
                title={readOnly ? t("read_only_mode") : undefined}
            >
                {t("edit_user")}
            </button>
        );

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-card modal-card--wide" onClick={(e) => e.stopPropagation()}>
                <div className="modal-card-header">
                    <h2>{t("edit_user")}: {user.name}</h2>
                    <button className="modal-close" onClick={handleClose}>
                        <X size={18} />
                    </button>
                </div>
                <div className="modal-card-body">

                {(showForceChange || showResetAction || showSuspendToggle || showDelete) && (
                    <div className="user-edit-actions" role="toolbar" aria-label={t("users_quick_actions")}>
                        {showForceChange && (
                            <button
                                type="button"
                                className="user-edit-action"
                                onClick={() => requestForce(user)}
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                aria-label={t("users_force_change")}
                                title={readOnly ? t("read_only_mode") : t("users_force_change")}
                            >
                                <KeyRound size={16} />
                                <span>{t("users_force_change")}</span>
                            </button>
                        )}
                        {showResetAction && (
                            <button
                                type="button"
                                className={`user-edit-action${showReset ? " user-edit-action--active" : ""}`}
                                onClick={() => set_showReset(s => !s)}
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                aria-pressed={showReset}
                                aria-label={t("users_reset_password")}
                                title={readOnly ? t("read_only_mode") : t("users_reset_password")}
                            >
                                <LockKeyhole size={16} />
                                <span>{t("users_reset_password")}</span>
                            </button>
                        )}
                        {showSuspendToggle && (
                            <button
                                type="button"
                                className={`user-edit-action${isSuspended ? " user-edit-action--suspended" : ""}`}
                                onClick={() => isSuspended ? requestUnsuspend(user) : requestSuspend(user)}
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                aria-label={t(isSuspended ? "users_unsuspend" : "users_suspend")}
                                title={readOnly ? t("read_only_mode") : t(isSuspended ? "users_unsuspend" : "users_suspend")}
                            >
                                <UserX size={16} />
                                <span>{t(isSuspended ? "users_unsuspend" : "users_suspend")}</span>
                            </button>
                        )}
                        {showDelete && (
                            <button
                                type="button"
                                className="user-edit-action user-edit-action--delete"
                                onClick={() => { handleClose(); toggleDelete(user); }}
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                aria-label={t("delete")}
                                title={readOnly ? t("read_only_mode") : t("delete")}
                            >
                                <Trash2 size={16} />
                                <span>{t("delete")}</span>
                            </button>
                        )}
                    </div>
                )}

                {twoFactorApplies && (
                    <div className="user-edit-tfa" role="group" aria-label={t("users_two_factor")}>
                        {user.twoFactorEnrolledAt
                            ? <ShieldCheck size={16} className="user-edit-tfa-icon user-edit-tfa-icon--on" aria-hidden="true" />
                            : <ShieldOff size={16} className="user-edit-tfa-icon" aria-hidden="true" />}
                        <span className="user-edit-tfa-text">
                            {user.twoFactorEnrolledAt
                                ? t("users_two_factor_status_enrolled", { date: enrolledOn })
                                : t("users_two_factor_status_pending")}
                        </span>
                        {showTwoFactorReset && (
                            <button
                                type="button"
                                className="user-edit-tfa-reset"
                                onClick={() => set_tfaDialog(true)}
                                disabled={readOnly}
                                aria-disabled={readOnly}
                                title={readOnly ? t("read_only_mode") : undefined}
                            >
                                {t("users_reset_two_factor")}
                            </button>
                        )}
                    </div>
                )}
                {tfaDialog && resetTwoFactor && (
                    <TwoFactorResetDialog user={user} onClose={() => set_tfaDialog(false)} resetTwoFactor={resetTwoFactor} />
                )}

                {showReset ? (
                    /* Reset-password panel replaces the main form while open, so
                       exactly one primary action is visible at a time. */
                    <form className="modal-form user-edit-reset" onSubmit={handleResetSubmit}>
                        <p className="user-edit-check-hint">{t("users_reset_password_hint", { name: user.name })}</p>
                        <PasswordFields
                            variant="light"
                            policy={resetPolicy}
                            context={resetContext}
                            newPassword={resetValue}
                            onNewPasswordChange={set_resetValue}
                            requireConfirm={false}
                            showGenerator
                            defaultRevealed
                            autoCompleteMode="off"
                            disabled={resetting}
                            autoFocus
                        />
                        <label className="user-edit-check">
                            <input
                                type="checkbox"
                                checked={requireChange}
                                onChange={(e) => set_requireChange(e.target.checked)}
                                disabled={resetting}
                            />
                            <span>
                                <span className="modal-form-label">{t("users_reset_require_change")}</span>
                            </span>
                        </label>
                        <div className="user-edit-reset-actions">
                            <button
                                type="submit"
                                className="btn btn-primary btn-sm"
                                disabled={resetting || readOnly || !isPolicyCompliant(resetValue, resetPolicy, resetContext)}
                            >
                                {resetting ? t("saving") : t("users_reset_password_save")}
                            </button>
                            <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => { set_showReset(false); set_resetValue(""); }}
                                disabled={resetting}
                            >
                                {t("cancel")}
                            </button>
                        </div>
                    </form>
                ) : (
                <form className="modal-form modal-form--grid" onSubmit={handleSubmit}>
                    <label>
                        <span className="modal-form-label">{t("name")}</span>
                        <input type="text" value={userName} onChange={(e) => set_userName(e.target.value)} />
                    </label>
                    <label>
                        <span className="modal-form-label">{t("users_col_username")}</span>
                        <input
                            type="text"
                            name="login-username"
                            autoComplete="off"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            maxLength={USERNAME_MAX}
                            value={username}
                            onChange={(e) => set_username(normaliseUsername(e.target.value))}
                        />
                        {usernameHint()}
                    </label>
                    <label>
                        <span className="modal-form-label">{t("warehouse")}</span>
                        <select value={userWarehouse} onChange={(e) => set_userWarehouse(e.target.value)}>
                            <option value="Pembroke">{t("pembroke_dock")}</option>
                            <option value="Newport">{t("newport")}</option>
                            <option value="Poland">{t("poland")}</option>
                        </select>
                    </label>
                    <label>
                        <span className="modal-form-label">{t("users_col_permissions")}</span>
                        {/* Your own position and an Admin's are locked (the BE pins both);
                            otherwise only positions the viewer outranks are offered. */}
                        {positionLocked ? (
                            <select value={userPermissions} disabled title={t("users_permissions_locked")}>
                                <option value={userPermissions}>{t(positionLabelKey((normalizePosition(userPermissions) ?? "Operative") as typeof POSITIONS[number]))}</option>
                            </select>
                        ) : (
                            <select value={userPermissions} onChange={(e) => set_userPermissions(e.target.value)}>
                                {positionOptions.map(p => (
                                    <option key={p} value={p}>{t(positionLabelKey(p))}</option>
                                ))}
                            </select>
                        )}
                    </label>
                    <label>
                        <span className="modal-form-label">{editedManagerPlus ? t("email_required") : t("email_optional")}</span>
                        <input type="email" name="email" autoComplete="off" placeholder={t("email_placeholder")} value={userEmail} onChange={(e) => set_userEmail(e.target.value)} />
                        {/* Soft hint only: the BE doesn't require it here, and the user is
                            asked for it at their next login (account setup). */}
                        {editedManagerPlus && !isValidEmail(emailTrimmed) && (
                            <span className="user-edit-field-hint">{t("users_email_required_manager")}</span>
                        )}
                    </label>
                    <label>
                        <span className="modal-form-label">{t("rfid_optional")}</span>
                        <input type="text" name="rfid" value={userRFID} onChange={(e) => setUserRFID(e.target.value)} />
                    </label>
                    {showConcurrentToggle && (
                        <label className="user-edit-check modal-form-span">
                            <input
                                type="checkbox"
                                checked={allowConcurrent}
                                onChange={(e) => set_allowConcurrent(e.target.checked)}
                            />
                            <span>
                                <span className="modal-form-label">{t("users_allow_concurrent")}</span>
                                <span className="user-edit-check-hint">{t("users_allow_concurrent_hint")}</span>
                            </span>
                        </label>
                    )}
                    {submitButton}
                </form>
                )}
                </div>
            </div>
        </div>
    )

};

export default EditUser;
